import React, { useState, useEffect } from 'react';
import {
  FileText,
  Clock,
  Building,
  DollarSign,
  Package,
  Calendar,
  MessageSquare,
  Mail,
  Hash,
  Users,
  Loader2,
  AlertCircle,
  Eye,
  Badge
} from 'lucide-react';
import { clsx } from 'clsx';

interface CustomerEnquiry {
  enquiry_id: string;
  customer_number: string;
  customer_email: string;
  company_name: string;
  budget_per_pack: string;
  number_of_packs: number;
  occasion: string;
  requirements: string;
  enquiry_date: string;
  enquiry_time: string;
  is_return_customer: boolean;
  status: string;
}

interface QuotationReviewProps {
  onBack?: () => void;
}

export const QuotationReview: React.FC<QuotationReviewProps> = ({ onBack }) => {
  const [enquiries, setEnquiries] = useState<CustomerEnquiry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedEnquiry, setSelectedEnquiry] = useState<CustomerEnquiry | null>(null);

  useEffect(() => {
    fetchEnquiries();
  }, []);

  const fetchEnquiries = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/customer-enquiries');
      const data = await response.json();
      setEnquiries(data.enquiries || []);
    } catch (err) {
      setError('Failed to load customer enquiries');
    } finally {
      setLoading(false);
    }
  };

  const getCustomerTypeColor = (isReturn: boolean) => {
    return isReturn 
      ? 'bg-blue-100 text-blue-800' 
      : 'bg-green-100 text-green-800';
  };

  const getCustomerTypeText = (isReturn: boolean) => {
    return isReturn ? 'Return Customer' : 'New Customer';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 size={32} className="animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading customer enquiries...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle size={32} className="text-red-600 mx-auto mb-4" />
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={fetchEnquiries}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              {onBack && (
                <button
                  onClick={onBack}
                  className="mr-4 p-2 text-gray-400 hover:text-gray-600"
                >
                  ←
                </button>
              )}
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <Users size={18} className="text-blue-600" />
                </div>
                <div>
                  <h1 className="text-lg font-semibold text-gray-900">Staff Review</h1>
                  <p className="text-sm text-gray-500">Customer Enquiries Management</p>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-500">
                {enquiries.length} enquiries
              </span>
              <button
                onClick={fetchEnquiries}
                className="p-2 text-gray-400 hover:text-gray-600"
                title="Refresh"
              >
                <Clock size={18} />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {enquiries.length === 0 ? (
          <div className="text-center py-12">
            <Users size={48} className="text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Customer Enquiries</h3>
            <p className="text-gray-500">
              New customer enquiries will appear here for staff review.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Enquiries List */}
            <div className="space-y-4">
              <h2 className="text-lg font-medium text-gray-900">Customer Enquiries</h2>
              {enquiries.map((enquiry) => (
                <div
                  key={enquiry.enquiry_id}
                  className={clsx(
                    'bg-white rounded-lg border p-4 cursor-pointer transition-all',
                    selectedEnquiry?.enquiry_id === enquiry.enquiry_id
                      ? 'border-blue-500 shadow-md'
                      : 'border-gray-200 hover:border-gray-300'
                  )}
                  onClick={() => setSelectedEnquiry(enquiry)}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <Hash size={14} className="text-gray-400" />
                        <span className="font-medium text-gray-900 text-sm">
                          {enquiry.customer_number}
                        </span>
                        <span className={clsx(
                          'px-2 py-1 rounded-full text-xs font-medium',
                          getCustomerTypeColor(enquiry.is_return_customer)
                        )}>
                          {getCustomerTypeText(enquiry.is_return_customer)}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2 mb-1">
                        <Building size={14} className="text-gray-500" />
                        <h3 className="font-medium text-gray-900 text-sm">
                          {enquiry.company_name}
                        </h3>
                      </div>
                      <div className="flex items-center space-x-2 text-xs text-gray-500">
                        <Mail size={12} />
                        <span>{enquiry.customer_email}</span>
                      </div>
                    </div>
                    <div className="flex flex-col items-end text-xs text-gray-500">
                      <div className="flex items-center space-x-1">
                        <Calendar size={12} />
                        <span>{enquiry.enquiry_date}</span>
                      </div>
                      <div className="flex items-center space-x-1 mt-1">
                        <Clock size={12} />
                        <span>{enquiry.enquiry_time}</span>
                      </div>
                    </div>
                  </div>

                  {/* Quick Info Grid */}
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div className="flex items-center space-x-2">
                      <Package size={12} className="text-gray-400" />
                      <span>{enquiry.number_of_packs} packs</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <DollarSign size={12} className="text-gray-400" />
                      <span>{enquiry.budget_per_pack}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <FileText size={12} className="text-gray-400" />
                      <span>{enquiry.occasion}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Badge size={12} className="text-gray-400" />
                      <span>{enquiry.status.replace('_', ' ')}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Detailed Enquiry Panel */}
            <div className="lg:sticky lg:top-8">
              {selectedEnquiry ? (
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-lg font-medium text-gray-900">Enquiry Details</h2>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-500">
                        {selectedEnquiry.customer_number}
                      </span>
                      <span className={clsx(
                        'px-2 py-1 rounded-full text-xs font-medium',
                        getCustomerTypeColor(selectedEnquiry.is_return_customer)
                      )}>
                        {getCustomerTypeText(selectedEnquiry.is_return_customer)}
                      </span>
                    </div>
                  </div>

                  {/* Customer Information */}
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 gap-4">
                      {/* Customer Email */}
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <div className="flex items-center space-x-2 mb-1">
                          <Mail size={16} className="text-gray-500" />
                          <span className="text-sm font-medium text-gray-700">Customer Email</span>
                        </div>
                        <p className="text-gray-900 font-medium">{selectedEnquiry.customer_email}</p>
                      </div>

                      {/* Company Name */}
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <div className="flex items-center space-x-2 mb-1">
                          <Building size={16} className="text-gray-500" />
                          <span className="text-sm font-medium text-gray-700">Company Name</span>
                        </div>
                        <p className="text-gray-900 font-medium">{selectedEnquiry.company_name}</p>
                      </div>

                      {/* Budget Per Pack */}
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <div className="flex items-center space-x-2 mb-1">
                          <DollarSign size={16} className="text-gray-500" />
                          <span className="text-sm font-medium text-gray-700">Budget Per Pack</span>
                        </div>
                        <p className="text-gray-900 font-medium">{selectedEnquiry.budget_per_pack}</p>
                      </div>

                      {/* Number of Packs */}
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <div className="flex items-center space-x-2 mb-1">
                          <Package size={16} className="text-gray-500" />
                          <span className="text-sm font-medium text-gray-700">Number of Packs</span>
                        </div>
                        <p className="text-gray-900 font-medium">{selectedEnquiry.number_of_packs} packs</p>
                      </div>

                      {/* Occasion */}
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <div className="flex items-center space-x-2 mb-1">
                          <Calendar size={16} className="text-gray-500" />
                          <span className="text-sm font-medium text-gray-700">Occasion</span>
                        </div>
                        <p className="text-gray-900 font-medium">{selectedEnquiry.occasion || 'Not specified'}</p>
                      </div>

                      {/* Requirements */}
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <div className="flex items-center space-x-2 mb-1">
                          <MessageSquare size={16} className="text-gray-500" />
                          <span className="text-sm font-medium text-gray-700">Requirements</span>
                        </div>
                        <p className="text-gray-900">{selectedEnquiry.requirements || 'No specific requirements'}</p>
                      </div>

                      {/* Enquiry Date & Time */}
                      <div className="bg-blue-50 p-3 rounded-lg">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <div className="flex items-center space-x-2 mb-1">
                              <Calendar size={16} className="text-blue-600" />
                              <span className="text-sm font-medium text-blue-700">Enquiry Date</span>
                            </div>
                            <p className="text-blue-900 font-medium">{selectedEnquiry.enquiry_date}</p>
                          </div>
                          <div>
                            <div className="flex items-center space-x-2 mb-1">
                              <Clock size={16} className="text-blue-600" />
                              <span className="text-sm font-medium text-blue-700">Enquiry Time</span>
                            </div>
                            <p className="text-blue-900 font-medium">{selectedEnquiry.enquiry_time}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-lg border border-gray-200 p-6 text-center">
                  <Eye size={32} className="text-gray-400 mx-auto mb-4" />
                  <h3 className="font-medium text-gray-900 mb-2">Select an Enquiry</h3>
                  <p className="text-sm text-gray-500">
                    Choose a customer enquiry from the list to view the details.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};